/* ═══════════════════════════════════════════════════════════════
   Design brief widget (Project Canvas R1, T2) — scope, applicable
   codes, occupancy, construction type, owner/design criteria, square
   footage, and migration-055 custom title/value fields, moved
   verbatim out of the old ProjectPanel.tsx chrome. Manage opens the
   same DesignBriefPanel Sheet (incl. custom fields). Same permissions
   (`ctx.can.edit`).

   Per-section collapse/reorder retired (Decision 12). Manage is now
   the kit's icon-only WidgetActionButton, same "Manage brief" label
   carried as its aria-label/tooltip.
   ═══════════════════════════════════════════════════════════════ */
import React, { useState } from 'react';
import { AlertTriangle, Settings2 } from 'lucide-react';
import type { ProjectDesignBrief } from '../../../../shared/entities';
import { Fields, WidgetFrame, WidgetActionButton } from '../../../components/widgets';
import type { FieldDef } from '../../../components/widgets';
import { Icon } from '../../../components/ui/Icon';
import { Hint } from '../../../components/ui/tooltip';
import EmptyState from '../../../components/EmptyState';
import { rem } from '../../../lib/rem';
import { DesignBriefPanel } from '../panels/DesignBriefPanel';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';

function SectionLoading() {
  return (
    <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }} role="status">
      Loading…
    </div>
  );
}

function blankBrief(projectId: string): ProjectDesignBrief {
  return { id: '', projectId, applicableCodes: [], createdAt: '', updatedAt: '' };
}

function DesignBriefBody({ ctx }: { ctx: ProjectWidgetCtx }) {
  const brief = ctx.designBrief.data;
  const customFields = ctx.designBriefFields.data;
  const canEdit = ctx.can.edit;

  if (ctx.designBrief.status === 'loading' || ctx.designBriefFields.status === 'loading') {
    return <SectionLoading />;
  }
  if (ctx.designBrief.status === 'error') {
    return (
      <EmptyState
        compact
        icon={<Icon icon={AlertTriangle} em={1.75} className="text-danger" />}
        title={ctx.designBrief.error || 'Could not load the design brief.'}
        action={{ label: 'Try again', onClick: () => void ctx.designBrief.reload() }}
      />
    );
  }
  if (ctx.designBriefFields.status === 'error') {
    return (
      <EmptyState
        compact
        icon={<Icon icon={AlertTriangle} em={1.75} className="text-danger" />}
        title={ctx.designBriefFields.error || 'Could not load the custom fields.'}
        action={{ label: 'Try again', onClick: () => void ctx.designBriefFields.reload() }}
      />
    );
  }
  const save = (patch: Partial<ProjectDesignBrief>) =>
    ctx.saveDesignBrief({ ...(brief || blankBrief(ctx.project.id)), ...patch });

  const noop = async () => {};
  const fields: FieldDef[] = [
    {
      key: 'scope',
      label: 'Scope',
      value: brief?.scope || null,
      editor: { kind: 'textarea' },
      canEdit,
      onSave: (v) => save({ scope: v }),
    },
    {
      key: 'applicableCodes',
      label: 'Applicable codes',
      value: null,
      display:
        brief && brief.applicableCodes.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: rem(6) }}>
            {brief.applicableCodes.map((c, i) => (
              <Hint key={`${c.code}-${i}`} text={c.note}>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    padding: `${rem(2)} ${rem(8)}`,
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--sa)',
                    border: '1px solid var(--bd)',
                    fontWeight: 600,
                  }}
                >
                  {c.code}
                </span>
              </Hint>
            ))}
          </div>
        ) : (
          '–'
        ),
      canEdit: false,
      onSave: noop,
    },
    {
      key: 'occupancy',
      label: 'Occupancy',
      value: brief?.occupancy || null,
      canEdit,
      onSave: (v) => save({ occupancy: v }),
    },
    {
      key: 'constructionType',
      label: 'Construction type',
      value: brief?.constructionType || null,
      canEdit,
      onSave: (v) => save({ constructionType: v }),
    },
    {
      key: 'ownerPreferences',
      label: 'Owner preferences',
      value: brief?.ownerPreferences || null,
      editor: { kind: 'textarea' },
      canEdit,
      onSave: (v) => save({ ownerPreferences: v }),
    },
    {
      key: 'designCriteria',
      label: 'Design criteria',
      value: brief?.designCriteria || null,
      editor: { kind: 'textarea' },
      canEdit,
      onSave: (v) => save({ designCriteria: v }),
    },
    {
      key: 'squareFootage',
      label: 'Square footage',
      value: brief?.squareFootage != null ? String(brief.squareFootage) : null,
      display: brief?.squareFootage != null ? `${brief.squareFootage.toLocaleString()} sq ft` : '–',
      editor: { kind: 'number' },
      canEdit,
      onSave: (v) => save({ squareFootage: v ? Number(v) : undefined }),
    },
    // Custom fields (migration 055) — in sortOrder, value editable inline;
    // title/add/remove/reorder live in the "Manage brief" panel.
    ...customFields.map(
      (f): FieldDef => ({
        key: `customField:${f.id}`,
        label: f.title,
        value: f.value ?? null,
        canEdit,
        onSave: (v) => ctx.saveDesignBriefField({ ...f, value: v }).then(() => undefined),
      })
    ),
  ];

  return <Fields fields={fields} density="compact" />;
}

function DesignBriefCard({ ctx }: { ctx: ProjectWidgetCtx }) {
  const [manageBriefOpen, setManageBriefOpen] = useState(false);
  return (
    <>
      <WidgetFrame
        title="Design brief"
        status="ready"
        form="fields"
        actions={
          ctx.can.edit ? (
            <WidgetActionButton
              icon={Settings2}
              label="Manage brief"
              onClick={() => setManageBriefOpen(true)}
            />
          ) : undefined
        }
      >
        <DesignBriefBody ctx={ctx} />
      </WidgetFrame>

      <DesignBriefPanel
        ctx={ctx}
        open={manageBriefOpen}
        onClose={() => setManageBriefOpen(false)}
      />
    </>
  );
}

export const designBriefWidget: ProjectWidgetDef = {
  id: 'project_brief',
  title: 'Design brief',
  description: 'Scope, applicable codes, occupancy, construction type, criteria, square footage.',
  category: 'overview',
  cols: { min: 6, max: 12 },
  defaultSize: 'standard',
  inputSurface: true,
  render: (ctx) => <DesignBriefCard ctx={ctx} />,
};
